import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import useSeo from '@/hooks/useSeo';
import { genres } from '@/mocks/manhwa';

const values = [
  {
    icon: 'ri-compass-3-line',
    title: '清晰定位',
    desc: '我们致力于把海量韩漫按恋爱、都市、奇幻、校园、悬疑、热血等题材系统整理，让每一位读者都能快速找到想看的作品。',
  },
  {
    icon: 'ri-list-check-3',
    title: '真实整理',
    desc: '所有分类与推荐都经过编辑团队人工梳理，确保题材准确、信息可靠，帮助读者节省翻找与筛选的时间。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    desc: '我们持续跟进最新韩漫连载动态，不断补充与优化分类内容，让榜单和推荐始终保持新鲜与实用。',
  },
];

const steps = [
  { num: '01', title: '收集与甄别', desc: '广泛收集不同题材的韩漫信息，筛选出内容优质、口碑良好的作品。' },
  { num: '02', title: '题材归类', desc: '根据恋爱、都市、奇幻、校园、悬疑、热血等题材进行准确分类与标签整理。' },
  { num: '03', title: '编辑推荐', desc: '结合评分与读者反馈，精选高人气、高口碑的作品形成推荐榜单。' },
];

export default function About() {
  useSeo({
    title: '关于我们 - 漫画韩漫网址',
    description:
      '漫画韩漫网址是一家专注于韩漫题材分类的内容导航站，按恋爱、都市、奇幻、校园、悬疑、热血整理优质韩国漫画，帮助读者快速定位想看的韩漫。',
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 漫画韩漫网址',
            url: 'https://suspensek.github.io/about',
            description: '漫画韩漫网址的品牌介绍、定位、发展理念与内容原则。',
            mainEntity: {
              '@type': 'Organization',
              name: '漫画韩漫网址',
              slogan: '一站式找到你想看的韩漫',
            },
          }),
        }}
      />
      <Navbar />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24 bg-background-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="面包屑导航" className="mb-8">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
              </li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-800 whitespace-nowrap">关于我们</li>
            </ol>
          </nav>

          <header className="max-w-3xl mb-14">
            <p className="text-xs font-medium tracking-widest text-accent-600 mb-3">ABOUT US</p>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950">关于漫画韩漫网址</h1>
            <p className="mt-5 text-foreground-600 leading-relaxed">
              漫画韩漫网址是一家专注于韩漫内容分类的导航站。我们相信，每一部好漫画都值得被快速找到。通过按题材系统整理，我们把海量韩漫变得清晰、有序、触手可及。
            </p>
          </header>

          {/* 品牌定位 */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20reading%20desk%20with%20tablet%20showing%20colorful%20korean%20webtoon%20art%2C%20warm%20cream%20and%20teal%20palette%2C%20soft%20natural%20light%2C%20minimal%20editorial%20photography%2C%20clean%20composition&width=1200&height=800&seq=manhwa-about-01&orientation=landscape"
                alt="关于漫画韩漫网址的阅读场景"
                title="漫画韩漫网址 - 让找漫更简单"
                className="w-full h-full object-cover aspect-[3/2]"
              />
            </div>
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-5">我们的定位</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                我们并不只是一个漫画列表，而是一个"以题材为中心的韩漫导航站"。无论你是想找轻松治愈的恋爱韩漫，还是紧张烧脑的悬疑韩漫，都能在对应的分类里快速锁定目标。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                截至目前，网站已按恋爱、都市、奇幻、校园、悬疑、热血六大题材收录了数千部韩漫，持续为读者提供清晰、可信的分类推荐。
              </p>
            </div>
          </section>

          {/* 核心价值观 */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">我们的价值理念</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {values.map((v) => (
                <div key={v.title} className="bg-background-100/70 border border-background-200 rounded-2xl p-6">
                  <span className="w-12 h-12 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="font-medium text-foreground-950 text-lg">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 发展理念 / 工作流程 */}
          <section className="mb-16 bg-primary-50 border border-primary-200 rounded-3xl p-8 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">我们如何整理内容</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((s) => (
                <div key={s.num} className="relative">
                  <span className="font-display-latin text-4xl text-primary-300 block mb-3">{s.num}</span>
                  <h3 className="font-medium text-foreground-950">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 六大题材 */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">覆盖的题材范围</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {genres.map((g) => (
                <Link
                  key={g.id}
                  to={`/category/${g.id}`}
                  className="bg-background-50 border border-background-200 rounded-2xl p-5 text-center hover:border-primary-300 transition-colors"
                >
                  <span className="w-11 h-11 mx-auto rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center mb-3">
                    <i className={`${g.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <h3 className="font-medium text-foreground-950 text-sm">{g.name}韩漫</h3>
                  <p className="mt-1 text-xs text-foreground-500">{g.count} 部收录</p>
                </Link>
              ))}
            </div>
          </section>

          {/* 口碑 */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">读者怎么说</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '小满', role: '恋爱韩漫爱好者', text: '以前找韩漫全靠碰运气，现在按题材分类之后省事多了，推荐也很准。' },
                { name: '阿澈', role: '悬疑迷', text: '分类很清晰，悬疑题材下整理了不少好作品，追更也不容易错过。' },
                { name: 'Momo', role: '热血读者', text: '编辑推荐很良心，顺着榜单看了好几部，质量都很能打。' },
              ].map((t) => (
                <figure key={t.name} className="bg-background-100/70 border border-background-200 rounded-2xl p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
                  <blockquote className="mt-3 text-foreground-700 text-sm leading-relaxed">{t.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm font-medium">
                      {t.name[0]}
                    </span>
                    <div>
                      <p className="font-medium text-foreground-950 text-sm">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-primary-800 rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl text-background-50">想聊聊或推荐韩漫？</h2>
              <p className="mt-2 text-background-200 text-sm md:text-base">欢迎联系我们，分享你的想法与建议。</p>
            </div>
            <Link
              to="/contact"
              className="h-11 px-6 rounded-full bg-accent-500 text-background-50 text-sm font-medium flex items-center whitespace-nowrap hover:bg-accent-600 transition-colors"
            >
              联系我们
            </Link>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}